
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  layout: 'reference',
  description: 'Plaid Exchange Data Definitions',
  title: 'Data Definitions',
  section: 'Data Definitions'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const AggregatorSchema = makeShortcode("AggregatorSchema");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Data Definitions' subtitle={metadata.description} mdxType="Header" />
    <h2 {...{
      "id": "getting-started"
    }}>{`Getting started`}</h2>
    <p>{`Before building your Plaid Exchange integration, please review the data definitions here. This will help you ensure your data is correctly formatted and that type matching requirements are met. Use this guide in combination with the API Reference for data aggregation and errors.`}</p>
    <h5 {...{
      "id": "notes"
    }}>{`Notes`}</h5>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Non-required parameters may be left blank, sent as `}<inlineCode parentName="p">{`null`}</inlineCode>{`, or omitted.
We recommend sending a blank field, as this is slightly cleaner.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Some required parameters are nullable (`}<inlineCode parentName="p">{`description`}</inlineCode>{`, `}<inlineCode parentName="p">{`settled_at`}</inlineCode>{`).
In these cases, the parameter will be marked as nullable in the parameters list.`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "primitives"
    }}>{`Primitives`}</h2>
    <AggregatorSchema type='model' model='Primitives' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "identifiers"
    }}>{`Identifiers`}</h4>
    <p>{`Many compound definitions in this API will make use of ad hoc identifier types, e.g. `}<inlineCode parentName="p">{`accountId`}</inlineCode>{`, `}<inlineCode parentName="p">{`identityId`}</inlineCode>{`, `}<inlineCode parentName="p">{`transactionId`}</inlineCode>{`. These are string
values, expected to remain stable during the life of the associated entity, and enable us to reconcile entities across state transitions, e.g.
transactions moving from pending to posted state, or a customer’s personal name change.`}</p>
    <h4 {...{
      "id": "geolocation"
    }}>{`GeoLocation`}</h4>
    <AggregatorSchema type='model' model='GeoLocation' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "geocoordinates"
    }}>{`GeoCoordinates`}</h4>
    <AggregatorSchema type='model' model='GeoCoordinates' mdxType="AggregatorSchema" />
    <h2 {...{
      "id": "account"
    }}>{`Account`}</h2>
    <h4 {...{
      "id": "currencies"
    }}>{`Currencies`}</h4>
    <p>{`All account subtypes include `}<inlineCode parentName="p">{`currency`}</inlineCode>{` and `}<inlineCode parentName="p">{`non_iso_currency`}</inlineCode>{` properties to indicate the currencies in which their balances are denominated. One and only one of these two fields should be set, with the other being unset (not present). If the account is denominated in a currency recognized under `}<a parentName="p" {...{
        "href": "https://www.iso.org/iso-4217-currency-codes.html"
      }}>{`ISO-4217`}</a>{`, then currency should be set to that currency’s alphabetic code under that standard.`}</p>
    <p>{`However, if the balance is denominated in a non-ISO currency, e.g. Bitcoin, mileage, or reward points, then currency must be null and `}<inlineCode parentName="p">{`non_iso_currency`}</inlineCode>{` should be used instead. In this case, Plaid does not guarantee that balances across accounts, and across institutions, refer to the same currency even when they use the same `}<inlineCode parentName="p">{`non_iso_currency`}</inlineCode>{` values.`}</p>
    <h4 {...{
      "id": "account-ownership"
    }}>{`Account Ownership`}</h4>
    <p>{`When modeling account ownership, include only the identities that are interesting when understanding a user’s financial situation and capability. For example, joint account ownership between a husband and wife should be modeled as two owner_identity_ids. Business account ownership should be modeled as a single business owner (use `}<inlineCode parentName="p">{`BusinessIdentity`}</inlineCode>{`), which then has `}<inlineCode parentName="p">{`owner_identity_ids`}</inlineCode>{` related to it, representing the beneficial owners (those with at least a 25% stake). Identification of business-owned depository accounts is critical for funds transfer use cases (e.g. payments), e.g. in the US, ACH transfers are required to distinguish B2B transactions from individual or B2C transactions.`}</p>
    <p>{`Interested non-ownership identities should be included when doing so would further illustrate the user’s situation. For example, when the user is a trustee of an account, their identity should be included in `}<inlineCode parentName="p">{`non_owner_identity_ids`}</inlineCode>{`. Inclusion of personally identifying information should always be balanced against visibility and consent concerns. Identity information not necessary for the currently authorized user to transact against the account should be limited, and those identities should be modeled using BasicIdentity e.g. when the trustor on an account is transacting, identifying information about the trustee need not be extensive. Conversely, if the trustee were viewing the account, their entry in `}<inlineCode parentName="p">{`non_owner_identity_ids`}</inlineCode>{` can be more complete, and the trustor’s entry can be more limited.`}</p>
    <p>{`Plaid’s account ownership model is intended to support compliance with ownership guidelines set forth in the FDIC Publication, `}<a parentName="p" {...{
        "href": "https://www.fdic.gov/resources/deposit-insurance/brochures/insured-deposits/"
      }}>{`"Your Insured Deposits"`}</a>{` and in the US Treasury FinCEN publication, `}<a parentName="p" {...{
        "href": "https://www.fincen.gov/sites/default/files/2018-04/FinCEN_Guidance_CDD_FAQ_FINAL_508_2.pdf"
      }}>{`"FIN-2018-G001"`}</a>{` (Anti-Money Laundering Guidance document).`}</p>
    <AggregatorSchema type='model' model='AccountOwnership' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "account-status"
    }}>{`Account Status`}</h4>
    <AggregatorSchema type='model' model='AccountStatus' tableOnly={true} mdxType="AggregatorSchema" />
    <h5 {...{
      "id": "suspended-access"
    }}>{`Suspended Access`}</h5>
    <p>{`There are multiple statuses listed here which, at first glance, seem to describe nearly identical conditions. Although frozen, flagged, and locked all describe conditions where the account’s assets cannot be accessed until they are released, the reasons for suspension differ,`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`frozen`}</inlineCode>{` describes a condition in connection to legal action: use frozen to indicate than an account’s assets may be subject to seizure`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`flagged`}</inlineCode>{` describes a condition where account activity is suspicious, or non-compliant with the customer agreement: use flagged to indicate that the account may be at risk of closure`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`locked`}</inlineCode>{` describes a condition applied proactively to protect the account holder: use locked to indicate that assets are restricted to protect against loss`}</li>
    </ul>
    <h4 {...{
      "id": "account-type-and-subtype"
    }}>{`Account Type and Subtype`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Subtype`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Transaction Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`depository`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`cash management`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DepositoryTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Cash management account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`depository`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`cd`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DepositoryTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Cash deposit (CD)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`depository`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`checking`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DepositoryTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Checking account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`depository`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`savings`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DepositoryTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Savings account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`depository`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`money market`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DepositoryTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Money market account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`depository`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`health`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DepositoryTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Any health savings or reimbursement account, e.g. HSA, FSA, HRA etc.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`depository`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`prepaid`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DepositoryTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Prepaid account, typically debit card`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`depository`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`gic`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DepositoryTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Guaranteed investment certificate (Canada)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`loan`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`auto`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`LoanTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Auto loan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`loan`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`commercial`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`LoanTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Commercial loan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`loan`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`construction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`LoanTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Construction loan, e.g. 203(k)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`loan`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`consumer`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`LoanTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Consumer installment loan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`loan`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`credit card`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`CreditTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Credit card`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`loan`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`home equity`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`LoanTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Loan or line of credit against home collateral, e.g. a HELOC`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`loan`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`mortgage`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`LoanTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Home mortgage`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`loan`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`overdraft`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`LoanTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Overdraft protection line of credit`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`loan`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`line of credit`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`CreditTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Line of credit`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`loan`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`student`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`LoanTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Student loan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`401a`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`IRC 401(a) governmental and nonprofit employee retirement plan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`401k`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`IRC 401(k) retirement plan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`403b`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`IRC 403(b) annuity retirement plan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`457b`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`IRC 457(b) retirement savings account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`529`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`IRC 529 educational savings plan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`brokerage`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Ordinary investment account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`esa`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Non-529 education savings account (e.g. Coverdell)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`ira`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Traditional IRA`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`isa`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Individual savings account (UK)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`lira`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Locked-in retirement account (Canada)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`other`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Other investment vehicle not covered here`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`rif`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Retirement income fund, includes LIF, LRIF, RRIF, PRIF and other income funds (Canada)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`rsp`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Retirement savings plan, includes RRSP, RDSP, RESP, LRSP and other savings plans (Canada)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pension`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Traditional defined-benefit plan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`profit-sharing`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Employee profit sharing plan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`roth ira`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Roth IRA`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`roth 401k`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Roth 401(k)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`sep ira`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Simplified employee plan IRA`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`simple ira`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SIMPLE IRA`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`sipp`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Self-invested personal pension`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`stock plan`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Stock purchase plan, e.g. ESPP`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`tsp`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Thrift savings plan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`tfsa`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Tax-free savings account (Canada)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`custodial`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account covered under UGMA/UTMA`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`investment`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`variable annuity`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`InvestmentTransaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Variable annuity tax-deferred retirement vehicle`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "baseaccount"
    }}>{`BaseAccount`}</h4>
    <AggregatorSchema type='model' model='BaseAccount' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "depositoryaccount"
    }}>{`DepositoryAccount`}</h4>
    <AggregatorSchema type='model' model='DepositoryAccount' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "depositoryaccountstatement"
    }}>{`DepositoryAccountStatement`}</h4>
    <AggregatorSchema type='model' model='DepositoryAccountStatement' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "transfercodemap"
    }}>{`TransferCodeMap`}</h4>
    <AggregatorSchema type='model' model='TransferCodeMap' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "depositoryaccounttransfercode"
    }}>{`DepositoryAccountTransferCode`}</h4>
    <AggregatorSchema type='model' model='DepositoryAccountTransferCode' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "achtransfercode"
    }}>{`AchTransferCode`}</h4>
    <AggregatorSchema type='model' model='AchTransferCode' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "efttransfercode"
    }}>{`EftTransferCode`}</h4>
    <AggregatorSchema type='model' model='EftTransferCode' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "ibantransfercode"
    }}>{`IbanTransferCode`}</h4>
    <AggregatorSchema type='model' model='IbanTransferCode' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "paymentcardtransfercode"
    }}>{`PaymentCardTransferCode`}</h4>
    <AggregatorSchema type='model' model='PaymentCardTransferCode' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "notionalaccount"
    }}>{`NotionalAccount`}</h4>
    <AggregatorSchema type='model' model='NotionalAccount' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "investmentaccount"
    }}>{`InvestmentAccount`}</h4>
    <AggregatorSchema type='model' model='InvestmentAccount' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "acatstransfercode"
    }}>{`AcatsTransferCode`}</h4>
    <AggregatorSchema type='model' model='AcatsTransferCode' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "security"
    }}>{`Security`}</h4>
    <AggregatorSchema type='model' model='Security' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "optionsecurity"
    }}>{`OptionSecurity`}</h4>
    <AggregatorSchema type='model' model='OptionSecurity' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "contracttype"
    }}>{`ContractType`}</h4>
    <AggregatorSchema type='model' model='ContractType' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "holding"
    }}>{`Holding`}</h4>
    <AggregatorSchema type='model' model='Holding' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "taxlot"
    }}>{`TaxLot`}</h4>
    <AggregatorSchema type='model' model='TaxLot' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "loanaccount"
    }}>{`LoanAccount`}</h4>
    <AggregatorSchema type='model' model='LoanAccount' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "loanstatement"
    }}>{`LoanStatement`}</h4>
    <AggregatorSchema type='model' model='LoanStatement' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "interestratetype"
    }}>{`InterestRateType`}</h4>
    <AggregatorSchema type='model' model='InterestRateType' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "interestrateperiod"
    }}>{`InterestRatePeriod`}</h4>
    <AggregatorSchema type='model' model='InterestRatePeriod' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "loanrepaymentstatus"
    }}>{`LoanRepaymentStatus`}</h4>
    <AggregatorSchema type='model' model='LoanRepaymentStatus' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "mortgageaccount"
    }}>{`MortgageAccount`}</h4>
    <AggregatorSchema type='model' model='MortgageAccount' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "studentloanaccount"
    }}>{`StudentLoanAccount`}</h4>
    <AggregatorSchema type='model' model='StudentLoanAccount' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "disbursement"
    }}>{`Disbursement`}</h4>
    <AggregatorSchema type='model' model='Disbursement' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "pslfeligibility"
    }}>{`PslfEligibility`}</h4>
    <AggregatorSchema type='model' model='PslfEligibility' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "creditcardaccount"
    }}>{`CreditCardAccount`}</h4>
    <AggregatorSchema type='model' model='CreditCardAccount' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "creditcardstatement"
    }}>{`CreditCardStatement`}</h4>
    <AggregatorSchema type='model' model='CreditCardStatement' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "creditcardinterestrate"
    }}>{`CreditCardInterestRate`}</h4>
    <AggregatorSchema type='model' model='CreditCardInterestRate' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "creditcardinterestratetype"
    }}>{`CreditCardInterestRateType`}</h4>
    <AggregatorSchema type='model' model='CreditCardInterestRateType' tableOnly={true} mdxType="AggregatorSchema" />
    <h2 {...{
      "id": "identity"
    }}>{`Identity`}</h2>
    <h4 {...{
      "id": "basicidentity"
    }}>{`BasicIdentity`}</h4>
    <AggregatorSchema type='model' model='BasicIdentity' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "fullidentity"
    }}>{`FullIdentity`}</h4>
    <AggregatorSchema type='model' model='FullIdentity' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "mailingaddress"
    }}>{`MailingAddress`}</h4>
    <AggregatorSchema type='model' model='MailingAddress' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "personalentity"
    }}>{`PersonalEntity`}</h4>
    <AggregatorSchema type='model' model='PersonalEntity' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "organizationalentity"
    }}>{`OrganizationalEntity`}</h4>
    <AggregatorSchema type='model' model='OrganizationalEntity' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "usbusinessstructure"
    }}>{`USBusinessStructure`}</h4>
    <AggregatorSchema type='model' model='USBusinessStructure' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "taxidentifier"
    }}>{`TaxIdentifier`}</h4>
    <AggregatorSchema type='model' model='TaxIdentifier' mdxType="AggregatorSchema" />
    <h2 {...{
      "id": "transaction"
    }}>{`Transaction`}</h2>
    <h4 {...{
      "id": "basetransaction"
    }}>{`BaseTransaction`}</h4>
    <AggregatorSchema type='model' model='BaseTransaction' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "depositoryorcredittransaction"
    }}>{`DepositoryOrCreditTransaction`}</h4>
    <AggregatorSchema type='model' model='DepositoryOrCreditTransaction' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "depositoryorcredittransactiontype"
    }}>{`DepositoryOrCreditTransactionType`}</h4>
    <AggregatorSchema type='model' model='DepositoryOrCreditTransactionTypeTableForDocs' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "depositoryorcredittransactionmethod"
    }}>{`DepositoryOrCreditTransactionMethod`}</h4>
    <AggregatorSchema type='model' model='DepositoryOrCreditTransactionMethodTableForDocs' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "investmenttransaction"
    }}>{`InvestmentTransaction`}</h4>
    <AggregatorSchema type='model' model='InvestmentTransaction' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "investmenttransactiontype"
    }}>{`InvestmentTransactionType`}</h4>
    <AggregatorSchema type='model' model='InvestmentTransactionTypeTableForDocs' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "investmenttransactionstatus"
    }}>{`InvestmentTransactionStatus`}</h4>
    <AggregatorSchema type='model' model='InvestmentTransactionStatusTableForDocs' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "loantransaction"
    }}>{`LoanTransaction`}</h4>
    <AggregatorSchema type='model' model='LoanTransaction' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "loantransactiontype"
    }}>{`LoanTransactionType`}</h4>
    <AggregatorSchema type='model' model='LoanTransactionTypeTableForDocs' tableOnly={true} mdxType="AggregatorSchema" />
    <h2 {...{
      "id": "acquisition"
    }}>{`Acquisition`}</h2>
    <h4 {...{
      "id": "plaidproductdatatype"
    }}>{`PlaidProductDataType`}</h4>
    <AggregatorSchema type='model' model='PlaidProductDataTypeTableForDocs' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "plaidproduct"
    }}>{`PlaidProduct`}</h4>
    <AggregatorSchema type='model' model='PlaidProduct' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "newaccountapplicant"
    }}>{`NewAccountApplicant`}</h4>
    <AggregatorSchema type='model' model='NewAccountApplicant' tableOnly={true} mdxType="AggregatorSchema" />
    <h2 {...{
      "id": "application"
    }}>{`Application`}</h2>
    <h4 {...{
      "id": "application-1"
    }}>{`Application`}</h4>
    <AggregatorSchema type='model' model='Application' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "connectedapplication"
    }}>{`ConnectedApplication`}</h4>
    <AggregatorSchema type='model' model='ConnectedApplication' mdxType="AggregatorSchema" />
    <h2 {...{
      "id": "scope"
    }}>{`Scope`}</h2>
    <h4 {...{
      "id": "scopes"
    }}>{`Scopes`}</h4>
    <AggregatorSchema type='model' model='Scopes' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "requestedscope"
    }}>{`RequestedScope`}</h4>
    <AggregatorSchema type='model' model='RequestedScopes' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "accountselectioncardinality"
    }}>{`AccountSelectionCardinality`}</h4>
    <AggregatorSchema type='model' model='AccountSelectionCardinality' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "accountaccess"
    }}>{`AccountAccess`}</h4>
    <AggregatorSchema type='model' model='AccountAccess' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "accountfilter"
    }}>{`AccountFilter`}</h4>
    <AggregatorSchema type='model' model='AccountFilter' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "accountfiltersubtypes"
    }}>{`AccountFilterSubtypes`}</h4>
    <AggregatorSchema type='model' model='AccountFilterSubtypes' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "productaccess"
    }}>{`ProductAccess`}</h4>
    <AggregatorSchema type='model' model='ProductAccess' mdxType="AggregatorSchema" />
    <h2 {...{
      "id": "errors"
    }}>{`Errors`}</h2>
    <h4 {...{
      "id": "basicerror"
    }}>{`BasicError`}</h4>
    <AggregatorSchema type='model' model='BasicError' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "authenticationerror"
    }}>{`AuthenticationError`}</h4>
    <AggregatorSchema type='model' model='AuthenticationError' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "institutionerror"
    }}>{`InstitutionError`}</h4>
    <AggregatorSchema type='model' model='InstitutionError' mdxType="AggregatorSchema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "getting-started",
        level: 2,
        title: "Getting started",
      },
{
        id: "primitives",
        level: 2,
        title: "Primitives",
      },
{
        id: "identifiers",
        level: 4,
        title: "Identifiers",
      },
{
        id: "geolocation",
        level: 4,
        title: "GeoLocation",
      },
{
        id: "geocoordinates",
        level: 4,
        title: "GeoCoordinates",
      },
{
        id: "account",
        level: 2,
        title: "Account",
      },
{
        id: "currencies",
        level: 4,
        title: "Currencies",
      },
{
        id: "account-ownership",
        level: 4,
        title: "Account Ownership",
      },
{
        id: "account-status",
        level: 4,
        title: "Account Status",
      },
{
        id: "account-type-and-subtype",
        level: 4,
        title: "Account Type and Subtype",
      },
{
        id: "baseaccount",
        level: 4,
        title: "BaseAccount",
      },
{
        id: "depositoryaccount",
        level: 4,
        title: "DepositoryAccount",
      },
{
        id: "depositoryaccountstatement",
        level: 4,
        title: "DepositoryAccountStatement",
      },
{
        id: "transfercodemap",
        level: 4,
        title: "TransferCodeMap",
      },
{
        id: "depositoryaccounttransfercode",
        level: 4,
        title: "DepositoryAccountTransferCode",
      },
{
        id: "achtransfercode",
        level: 4,
        title: "AchTransferCode",
      },
{
        id: "efttransfercode",
        level: 4,
        title: "EftTransferCode",
      },
{
        id: "ibantransfercode",
        level: 4,
        title: "IbanTransferCode",
      },
{
        id: "paymentcardtransfercode",
        level: 4,
        title: "PaymentCardTransferCode",
      },
{
        id: "notionalaccount",
        level: 4,
        title: "NotionalAccount",
      },
{
        id: "investmentaccount",
        level: 4,
        title: "InvestmentAccount",
      },
{
        id: "acatstransfercode",
        level: 4,
        title: "AcatsTransferCode",
      },
{
        id: "security",
        level: 4,
        title: "Security",
      },
{
        id: "optionsecurity",
        level: 4,
        title: "OptionSecurity",
      },
{
        id: "contracttype",
        level: 4,
        title: "ContractType",
      },
{
        id: "holding",
        level: 4,
        title: "Holding",
      },
{
        id: "taxlot",
        level: 4,
        title: "TaxLot",
      },
{
        id: "loanaccount",
        level: 4,
        title: "LoanAccount",
      },
{
        id: "loanstatement",
        level: 4,
        title: "LoanStatement",
      },
{
        id: "interestratetype",
        level: 4,
        title: "InterestRateType",
      },
{
        id: "interestrateperiod",
        level: 4,
        title: "InterestRatePeriod",
      },
{
        id: "loanrepaymentstatus",
        level: 4,
        title: "LoanRepaymentStatus",
      },
{
        id: "mortgageaccount",
        level: 4,
        title: "MortgageAccount",
      },
{
        id: "studentloanaccount",
        level: 4,
        title: "StudentLoanAccount",
      },
{
        id: "disbursement",
        level: 4,
        title: "Disbursement",
      },
{
        id: "pslfeligibility",
        level: 4,
        title: "PslfEligibility",
      },
{
        id: "creditcardaccount",
        level: 4,
        title: "CreditCardAccount",
      },
{
        id: "creditcardstatement",
        level: 4,
        title: "CreditCardStatement",
      },
{
        id: "creditcardinterestrate",
        level: 4,
        title: "CreditCardInterestRate",
      },
{
        id: "creditcardinterestratetype",
        level: 4,
        title: "CreditCardInterestRateType",
      },
{
        id: "identity",
        level: 2,
        title: "Identity",
      },
{
        id: "basicidentity",
        level: 4,
        title: "BasicIdentity",
      },
{
        id: "fullidentity",
        level: 4,
        title: "FullIdentity",
      },
{
        id: "mailingaddress",
        level: 4,
        title: "MailingAddress",
      },
{
        id: "personalentity",
        level: 4,
        title: "PersonalEntity",
      },
{
        id: "organizationalentity",
        level: 4,
        title: "OrganizationalEntity",
      },
{
        id: "usbusinessstructure",
        level: 4,
        title: "USBusinessStructure",
      },
{
        id: "taxidentifier",
        level: 4,
        title: "TaxIdentifier",
      },
{
        id: "transaction",
        level: 2,
        title: "Transaction",
      },
{
        id: "basetransaction",
        level: 4,
        title: "BaseTransaction",
      },
{
        id: "depositoryorcredittransaction",
        level: 4,
        title: "DepositoryOrCreditTransaction",
      },
{
        id: "depositoryorcredittransactiontype",
        level: 4,
        title: "DepositoryOrCreditTransactionType",
      },
{
        id: "depositoryorcredittransactionmethod",
        level: 4,
        title: "DepositoryOrCreditTransactionMethod",
      },
{
        id: "investmenttransaction",
        level: 4,
        title: "InvestmentTransaction",
      },
{
        id: "investmenttransactiontype",
        level: 4,
        title: "InvestmentTransactionType",
      },
{
        id: "investmenttransactionstatus",
        level: 4,
        title: "InvestmentTransactionStatus",
      },
{
        id: "loantransaction",
        level: 4,
        title: "LoanTransaction",
      },
{
        id: "loantransactiontype",
        level: 4,
        title: "LoanTransactionType",
      },
{
        id: "acquisition",
        level: 2,
        title: "Acquisition",
      },
{
        id: "plaidproductdatatype",
        level: 4,
        title: "PlaidProductDataType",
      },
{
        id: "plaidproduct",
        level: 4,
        title: "PlaidProduct",
      },
{
        id: "newaccountapplicant",
        level: 4,
        title: "NewAccountApplicant",
      },
{
        id: "application",
        level: 2,
        title: "Application",
      },
{
        id: "application-1",
        level: 4,
        title: "Application",
      },
{
        id: "connectedapplication",
        level: 4,
        title: "ConnectedApplication",
      },
{
        id: "scope",
        level: 2,
        title: "Scope",
      },
{
        id: "scopes",
        level: 4,
        title: "Scopes",
      },
{
        id: "requestedscope",
        level: 4,
        title: "RequestedScope",
      },
{
        id: "accountselectioncardinality",
        level: 4,
        title: "AccountSelectionCardinality",
      },
{
        id: "accountaccess",
        level: 4,
        title: "AccountAccess",
      },
{
        id: "accountfilter",
        level: 4,
        title: "AccountFilter",
      },
{
        id: "accountfiltersubtypes",
        level: 4,
        title: "AccountFilterSubtypes",
      },
{
        id: "productaccess",
        level: 4,
        title: "ProductAccess",
      },
{
        id: "errors",
        level: 2,
        title: "Errors",
      },
{
        id: "basicerror",
        level: 4,
        title: "BasicError",
      },
{
        id: "authenticationerror",
        level: 4,
        title: "AuthenticationError",
      },
{
        id: "institutionerror",
        level: 4,
        title: "InstitutionError",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
